import { useState, useRef, useEffect } from 'react';
import apiServices from '../../services/service';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loader from '../../shared/component/Loader';
import { getGridDataByModuleKey } from '../../redux/selector';
import { useDispatch, useSelector } from 'react-redux';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import { fetchDataAndUpdateStore } from '../../redux/actions';
import { contactInputData } from '../../shared/constants';
import useFetchIfNeeded from '../../redux/useFetchIfNeeded';
import { getMassEmailByModuleKey } from '../../redux/selector';

/* ---------- GrowthOS icons (24×24 stroke, strokeWidth 1.7) ---------- */
const Svg = ({ size = 18, children }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);
const IcDownload = (p) => (
  <Svg {...p}>
    <path d="M12 3v12M7 12l5 5 5-5M5 21h14" />
  </Svg>
);
const IcCheck = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 12l2.5 2.5 4.5-5" />
  </Svg>
);
const IcCalendar = (p) => (
  <Svg {...p}>
    <rect x="3" y="4.5" width="18" height="16.5" rx="2.5" />
    <path d="M16 2.5v4M8 2.5v4M3 9.5h18" />
  </Svg>
);
const IcUsers = (p) => (
  <Svg {...p}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13A4 4 0 0 1 16 11" />
  </Svg>
);
const IcTemplate = (p) => (
  <Svg {...p}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5M9 13h6M9 17h6" />
  </Svg>
);
const IcSearch = (p) => (
  <Svg {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4-4" />
  </Svg>
);
const IcFilter = (p) => (
  <Svg {...p}>
    <path d="M3 4h18l-7 8v6l-4 2v-8z" />
  </Svg>
);
const IcChevL = (p) => (
  <Svg {...p}>
    <path d="M15 18l-6-6 6-6" />
  </Svg>
);
const IcChevR = (p) => (
  <Svg {...p}>
    <path d="M9 18l6-6-6-6" />
  </Svg>
);
const IcChevD = (p) => (
  <Svg {...p}>
    <path d="M6 9l6 6 6-6" />
  </Svg>
);

/* ---------- helpers ---------- */
const fmtNum = (n) => (n == null ? '0' : Number(n).toLocaleString('en-US'));
const titleCase = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
const initials = (name) =>
  String(name || '')
    .replace(/@.*$/, '')
    .trim()
    .split(/[\s._-]+/)
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase() || '?';

const recipientCount = (toAddress) =>
  String(toAddress || '')
    .split(/[;,]/)
    .map((s) => s.trim())
    .filter(Boolean).length;

const fmtDate = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  if (isNaN(d.getTime())) return String(val);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const fmtTime = (val) => {
  if (!val) return '';
  const d = new Date(val);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const statusMeta = (s) => {
  const v = String(s || '').toLowerCase();
  if (v.includes('schedul')) return { label: 'Scheduled', cls: 'b-blue' };
  if (v.includes('sent') || v === 'success' || v.includes('deliver'))
    return { label: 'Sent', cls: 'b-green' };
  if (v.includes('draft')) return { label: 'Draft', cls: 'b-gray' };
  if (v.includes('fail') || v.includes('error') || v.includes('bounce') || v.includes('stop'))
    return { label: titleCase(s) || 'Failed', cls: 'b-red' };
  if (v.includes('pend') || v.includes('queue') || v.includes('process'))
    return { label: titleCase(s), cls: 'b-amber' };
  return { label: s ? titleCase(s) : 'Unknown', cls: 'b-gray' };
};

// compact page-number list with ellipses (ported from the GrowthOS design)
const pageList = (cur, totalP) => {
  if (totalP <= 7) return Array.from({ length: totalP }, (_, i) => i + 1);
  const out = [1];
  if (cur > 3) out.push('…');
  const s = Math.max(2, cur - 1);
  const e = Math.min(totalP - 1, cur + 1);
  for (let i = s; i <= e; i++) out.push(i);
  if (cur < totalP - 2) out.push('…');
  out.push(totalP);
  return out;
};

const StatCard = ({ icon: Icon, tone, value, label, sub }) => (
  <div className="gos-me-stat">
    <div className={`gos-me-stat-ic tone-${tone}`}>
      <Icon size={18} />
    </div>
    <div className="gos-me-stat-body">
      <div className="gos-me-stat-val">{value}</div>
      <div className="gos-me-stat-label">{label}</div>
      {sub ? <div className="gos-me-stat-sub">{sub}</div> : null}
    </div>
  </div>
);

const MassEmailPage = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const stateData = useSelector((state) => state);

  const isLeadPath = location.pathname.includes('lead');
  const moduleType = isLeadPath ? 'Lead' : 'Contact';
  const keySent = `massEmail${moduleType}Sent`;
  const keySchedule = `massEmail${moduleType}Schedule`;

  const [activeTab, setActiveTab] = useState('Sent');
  const activeKey = activeTab === 'Sent' ? keySent : keySchedule;

  // initial loads (only fetch when not already in the store)
  useFetchIfNeeded(getMassEmailByModuleKey, fetchDataAndUpdateStore, {
    key: 'massEmailLeadSent',
    inputData: { ...contactInputData, module: 'Lead', columnName: 'createdDateTime', status: '' },
  });
  useFetchIfNeeded(getMassEmailByModuleKey, fetchDataAndUpdateStore, {
    key: 'massEmailContactSent',
    inputData: { ...contactInputData, module: 'Contact', columnName: 'createdDateTime', status: '' },
  });

  const gridSent = useSelector(getGridDataByModuleKey(keySent));
  const gridSchedule = useSelector(getGridDataByModuleKey(keySchedule));
  const grid = activeTab === 'Sent' ? gridSent : gridSchedule;

  const rows = grid?.emails || [];
  const totalCount = grid?.totalCount || 0;
  const inputPayload = grid?.inputPayload || {};

  const totalCountSent = gridSent?.totalCount || 0;
  const totalCountSchedule = gridSchedule?.totalCount || 0;

  const loading = isLoadingByKey(stateData, activeKey);

  // lazy-load the Scheduled tab the first time it's opened
  useEffect(() => {
    if (activeTab === 'Scheduled' && !gridSchedule?.emails) {
      dispatch(
        fetchDataAndUpdateStore({
          key: keySchedule,
          inputData: {
            ...contactInputData,
            module: moduleType,
            columnName: 'createdDateTime',
            status: 'Scheduled',
          },
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  /* ---------- pagination (server-side, mirrors customStatusBar) ---------- */
  const pageSize =
    inputPayload?.pageSize || parseInt(process.env.REACT_APP_DEFAULT_PAGESIZE) || 20;
  const currentPage = inputPayload?.pageNumber || 1;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const startRow = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endRow = Math.min(currentPage * pageSize, totalCount);

  const basePayload = () =>
    inputPayload && Object.keys(inputPayload).length
      ? inputPayload
      : {
          ...contactInputData,
          module: moduleType,
          columnName: 'createdDateTime',
          status: activeTab === 'Scheduled' ? 'Scheduled' : '',
        };

  const fetchPage = (patch) =>
    dispatch(
      fetchDataAndUpdateStore({ key: activeKey, inputData: { ...basePayload(), ...patch } })
    );

  const goToPage = (p) => {
    if (p >= 1 && p <= totalPages && p !== currentPage) fetchPage({ pageNumber: p });
  };
  const changeSize = (s) => fetchPage({ pageNumber: 1, pageSize: Number(s) });

  /* ---------- search (server-side searchText, debounced) ---------- */
  const [q, setQ] = useState('');
  const searchTimer = useRef(null);
  useEffect(() => {
    setQ(inputPayload?.searchText || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const onSearch = (val) => {
    setQ(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      fetchPage({ pageNumber: 1, searchText: val ? val.trim() : null });
    }, 400);
  };

  /* ---------- export ---------- */
  const [exporting, setExporting] = useState(false);
  const exportAll = async () => {
    setExporting(true);
    const fetchData = {
      ...contactInputData,
      pageSize: totalCount || pageSize,
      module: moduleType,
      columnName: 'createdDateTime',
      status: activeTab === 'Scheduled' ? 'Scheduled' : '',
      searchText: q ? q.trim() : null,
    };
    try {
      const response = await apiServices.exportEmail(fetchData);
      if (response?.data && response.status === 200) {
        if (String(response.data).includes('Export initiated.')) {
          toast.warning(response.data);
        } else {
          const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `${moduleType} Emails.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      } else {
        toast.error('No data received from the server.');
      }
    } catch (error) {
      toast.error('Error exporting emails.');
    } finally {
      setExporting(false);
    }
  };

  /* ---------- derived stats ---------- */
  const recipientsThisPage = rows.reduce((s, r) => s + recipientCount(r.toAddress), 0);
  const templatesThisPage = new Set(
    rows.map((r) => r.templateName || r.subject).filter(Boolean)
  ).size;

  const tabs = [
    { id: 'Sent', label: 'Sent', n: totalCountSent },
    { id: 'Scheduled', label: 'Scheduled', n: totalCountSchedule },
  ];

  return (
    <>
      {(loading || exporting) && <Loader />}
      <div className="inner-page gos-mailpage">
        {/* page head */}
        <div className="gos-me-head">
          <div>
            <div className="gos-me-title">Mass Email</div>
            <div className="gos-me-sub">
              <strong>{fmtNum(totalCountSent)}</strong> campaigns sent ·{' '}
              <strong>{fmtNum(totalCountSchedule)}</strong> scheduled
            </div>
          </div>
          <button className="gos-me-export" type="button" onClick={exportAll}>
            <IcDownload size={16} />
            Export
          </button>
        </div>

        {/* stat strip */}
        <div className="gos-me-stats">
          <StatCard
            icon={IcCheck}
            tone="green"
            value={fmtNum(totalCountSent)}
            label="Emails Sent"
            sub={`${moduleType} campaigns`}
          />
          <StatCard
            icon={IcCalendar}
            tone="blue"
            value={fmtNum(totalCountSchedule)}
            label="Scheduled"
            sub="queued to send"
          />
          <StatCard
            icon={IcUsers}
            tone="amber"
            value={fmtNum(recipientsThisPage)}
            label="Recipients"
            sub="on this page"
          />
          <StatCard
            icon={IcTemplate}
            tone="gray"
            value={fmtNum(templatesThisPage)}
            label="Templates"
            sub="on this page"
          />
        </div>

        {/* grid card */}
        <div className="gos-me-card">
          <div className="gos-me-toolbar">
            <div className="gos-me-seg">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={'gos-me-seg-btn' + (activeTab === t.id ? ' on' : '')}
                  onClick={() => setActiveTab(t.id)}
                >
                  {t.label} <span className="gos-me-seg-n">{fmtNum(t.n)}</span>
                </button>
              ))}
            </div>
            <div className="gos-me-search">
              <IcSearch size={16} />
              <input
                placeholder="Search subject or recipient…"
                value={q}
                onChange={(e) => onSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="gos-me-tbl-scroll">
            <table className="gos-me-tbl">
              <thead>
                <tr>
                  <th>Campaign</th>
                  <th>Recipients</th>
                  <th>Status</th>
                  <th>Sender</th>
                  <th style={{ textAlign: 'right' }}>
                    {activeTab === 'Scheduled' ? 'Scheduled' : 'Sent'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e, i) => {
                  const st = statusMeta(e.status);
                  const sender = e.fromAddress || e.createdBy || '—';
                  const when = activeTab === 'Scheduled' ? e.scheduledTime : e.createdDateTime;
                  const tmpl = e.templateName || e.templateSubject;
                  return (
                    <tr key={e.id || i}>
                      <td>
                        <div className="gos-me-subj">{e.subject || '(no subject)'}</div>
                        <div className="gos-me-segline">
                          <IcFilter size={11} />
                          {recipientCount(e.toAddress)} recipient
                          {recipientCount(e.toAddress) === 1 ? '' : 's'}
                          {tmpl ? ` · ${tmpl}` : ''}
                        </div>
                      </td>
                      <td>
                        <span className="gos-me-recip">{fmtNum(recipientCount(e.toAddress))}</span>
                      </td>
                      <td>
                        <span className={`gos-me-badge ${st.cls}`}>
                          <span className="gos-me-pip" />
                          {st.label}
                        </span>
                      </td>
                      <td>
                        <div className="gos-me-sender">
                          <span className="gos-me-ava">{initials(sender)}</span>
                          <span className="gos-me-sender-name">{sender}</span>
                        </div>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="gos-me-date">{fmtDate(when)}</div>
                        <div className="gos-me-time">{fmtTime(when)}</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {rows.length === 0 && !loading ? (
              <div className="gos-me-empty">No emails to show.</div>
            ) : null}
          </div>

          {/* pager */}
          <div className="gos-pager gos-me-pager">
            <div className="pager-info">
              Showing <strong>{startRow}</strong>–<strong>{endRow}</strong> of{' '}
              <strong>{fmtNum(totalCount)}</strong> emails
            </div>
            <div className="pager-ctrls">
              <div className="pager-size">
                <span>Rows</span>
                <div className="pager-size-sel">
                  <select value={pageSize} onChange={(e) => changeSize(e.target.value)}>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="500">500</option>
                  </select>
                  <IcChevD size={16} />
                </div>
              </div>
              <div className="pager-pages">
                <button
                  className="pager-arrow"
                  disabled={currentPage <= 1}
                  onClick={() => goToPage(currentPage - 1)}
                  aria-label="Previous page"
                >
                  <IcChevL size={16} />
                </button>
                {pageList(currentPage, totalPages).map((p, idx) =>
                  p === '…' ? (
                    <span key={'e' + idx} className="pager-gap">
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      className={'pager-num' + (p === currentPage ? ' on' : '')}
                      onClick={() => goToPage(p)}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  className="pager-arrow"
                  disabled={currentPage >= totalPages}
                  onClick={() => goToPage(currentPage + 1)}
                  aria-label="Next page"
                >
                  <IcChevR size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MassEmailPage;
